// Kokoro TTS (82M, Apache-2.0) — best free, fully in-browser neural voice.
// Lazy-loaded from a CDN at runtime (the URL is built dynamically so esbuild
// leaves the import external and doesn't bundle ~MBs of transformers.js into
// application.js). Model weights download once (~80MB at q8) and cache in the
// browser's IndexedDB, so it's offline after the first use.
//
// IMPORTANT: Kokoro only engages when WebGPU is available. On the CPU/WASM
// path it blocks the main thread and produces slow, garbled ("mumbling +
// click") audio — so without WebGPU we stay on the browser's built-in voice.
//
// Public API:
//   KokoroTTS.preload()        → check WebGPU + start model download (idempotent)
//   KokoroTTS.ready()          → true once the model can synthesize on GPU
//   KokoroTTS.failed()         → true if WebGPU missing or load failed
//   KokoroTTS.status()         → "idle" | "loading" | "ready" | "failed"
//   KokoroTTS.progress()       → 0..100, latest model-download percent
//   KokoroTTS.synthesize(text) → Promise<Blob> (WAV) for one chunk

const MODEL = "onnx-community/Kokoro-82M-v1.0-ONNX"
const VOICE = "bm_lewis"           // British male — the Jarvis pick
const DTYPE = "fp32"               // GPU has the headroom; best fidelity
const SPEED = 1.25                 // >1 speaks faster; Jarvis should be brisk
const CDN_PARTS = [ "https://esm.sh", "kokoro-js@1" ] // joined at runtime → external import

let _instancePromise = null
let _ready = false
let _failed = false
let _loading = false
let _progress = 0

async function _hasWebGPU() {
  if (typeof navigator === "undefined" || !navigator.gpu) return false
  try {
    const adapter = await navigator.gpu.requestAdapter()
    return !!adapter
  } catch (_) {
    return false
  }
}

async function _load() {
  if (_instancePromise) return _instancePromise
  _loading = true
  _instancePromise = (async () => {
    if (!(await _hasWebGPU())) {
      throw new Error("WebGPU unavailable — staying on the browser voice")
    }
    const url = CDN_PARTS.join("/")
    const mod = await import(url)
    const KokoroTTS = mod.KokoroTTS || mod.default?.KokoroTTS
    if (!KokoroTTS) throw new Error("kokoro-js: KokoroTTS export not found")

    const tts = await KokoroTTS.from_pretrained(MODEL, {
      dtype: DTYPE,
      device: "webgpu",
      // transformers.js streams per-file download events; surface the latest %.
      progress_callback: (info) => {
        if (info && info.status === "progress" && typeof info.progress === "number") {
          _progress = Math.max(0, Math.min(100, Math.round(info.progress)))
        }
      }
    })
    // Warm the GPU pipeline (first generate compiles shaders — seconds) so the
    // first real sentence of a reply plays fast instead of after the whole
    // answer. Done before flipping `ready`, so until warm we use the instant
    // browser voice rather than a slow cold Kokoro synth.
    try { await tts.generate("Ready.", { voice: VOICE, speed: SPEED }) } catch (_) {}
    _ready = true
    _loading = false
    return tts
  })()
  _instancePromise.catch(() => { _failed = true; _loading = false })
  return _instancePromise
}

export const KokoroTTS = {
  preload() {
    if (!_instancePromise && !_failed) _load().catch(() => {})
  },

  ready() { return _ready },
  failed() { return _failed },
  progress() { return _progress },

  status() {
    if (_failed) return "failed"
    if (_ready) return "ready"
    if (_loading) return "loading"
    return "idle"
  },

  async synthesize(text) {
    const tts = await _load()
    const audio = await tts.generate(text, { voice: VOICE, speed: SPEED })
    return audio.toBlob() // WAV Blob
  },

  voiceName() { return VOICE }
}
